import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import './paper.css';
import { SITE_NAME, TAGLINE } from '../site';

const PAGE_SIZE = 12;
const TOP_TAGS = 16;

const tagsOf = (p) => p.tags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);

// 1 … 4 5 6 … 9
const pageList = (cur, last) => {
  const keep = [...new Set([1, cur - 1, cur, cur + 1, last])].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b);
  return keep.flatMap((n, i) => (i && n - keep[i - 1] > 1 ? ['…' + n, n] : [n]));
};

// Book-style home. Filter + page state lives in the URL (?tags=a,b&mode=any&q=text&page=2),
// so every view is linkable. /blog/type/:category still works and is folded into the tag filter.
const PaperHome = ({ posts }) => {
  const { category } = useParams();
  const history = useHistory();
  const params = new URLSearchParams(useLocation().search);

  const selected = [...new Set([
    ...(category ? [category.toLowerCase()] : []),
    ...(params.get('tags') || '').split(',').filter(Boolean),
  ])];
  const any = params.get('mode') === 'any';
  const q = params.get('q') || '';
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);

  const [showAllTags, setShowAllTags] = useState(false);
  const [tagQ, setTagQ] = useState('');

  const to = (over = {}) => {
    const s = { tags: selected, any, q, page: 1, ...over };
    const p = new URLSearchParams();
    if (s.tags.length) p.set('tags', s.tags.join(','));
    if (s.any) p.set('mode', 'any');
    if (s.q) p.set('q', s.q);
    if (s.page > 1) p.set('page', s.page);
    const search = p.toString();
    return { pathname: '/', search: search ? '?' + search : '' };
  };

  const tagCounts = useMemo(() => {
    const m = {};
    posts.forEach((p) => tagsOf(p).forEach((t) => { m[t] = (m[t] || 0) + 1; }));
    return Object.entries(m).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [posts]);

  const needle = q.trim().toLowerCase();
  const matched = posts.filter((p) => {
    const t = tagsOf(p);
    const tagOk = !selected.length || (any ? selected.some((s) => t.includes(s)) : selected.every((s) => t.includes(s)));
    return tagOk && (!needle || p.title.toLowerCase().includes(needle));
  });
  const pages = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const shown = matched.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  useEffect(() => { window.scrollTo(0, 0); }, [cur]);

  const tq = tagQ.trim().toLowerCase();
  const visibleTags = tq
    ? tagCounts.filter(([t]) => t.includes(tq))
    : showAllTags
      ? tagCounts
      : tagCounts.filter(([t], i) => i < TOP_TAGS || selected.includes(t));

  const toggle = (t) => (selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  const filtering = selected.length > 0 || needle;

  return (
    <div className="paper-home">
      <header className="paper-title">
        <h1><Link to="/">{SITE_NAME}</Link></h1>
        <p className="paper-tagline">{TAGLINE}</p>
        <nav>
          <Link to="/blog/me">Who</Link> · <Link to="/reading">Reading</Link> · <Link to="/blog/new">New</Link>
        </nav>
      </header>

      <section className="paper-filter">
        <h2 className="paper-heading">Index</h2>
        <div className="paper-filter-bar">
          <input
            type="search" placeholder="search titles" value={q} aria-label="Search titles"
            onChange={(e) => history.replace(to({ q: e.target.value }))}
          />
          <input
            type="search" placeholder="find a tag" value={tagQ} aria-label="Find a tag"
            onChange={(e) => setTagQ(e.target.value)}
          />
        </div>
        <ul className="paper-tags">
          {visibleTags.map(([t, n]) => (
            <li key={t}>
              <Link to={to({ tags: toggle(t) })} className={selected.includes(t) ? 'on' : ''} aria-pressed={selected.includes(t)}>
                {t}<sup>{n}</sup>
              </Link>
            </li>
          ))}
          {!tq && tagCounts.length > TOP_TAGS && (
            <li><button type="button" className="paper-link" onClick={() => setShowAllTags(!showAllTags)}>
              {showAllTags ? 'fewer' : `all ${tagCounts.length} tags`}
            </button></li>
          )}
          {tq && visibleTags.length === 0 && <li><em>no such tag</em></li>}
        </ul>
        {selected.length > 1 && (
          <p className="paper-mode">
            match{' '}
            <Link to={to({ any: false })} className={!any ? 'on' : ''}>all</Link> /{' '}
            <Link to={to({ any: true })} className={any ? 'on' : ''}>any</Link> of the chosen tags
          </p>
        )}
      </section>

      <h2 className="paper-heading">Contents</h2>
      <p className="paper-count">
        {filtering ? `${matched.length} of ${posts.length} posts · ` : `${posts.length} posts · `}
        page {cur} of {pages}
        {filtering && <> · <Link to="/" className="paper-clear">clear filters</Link></>}
      </p>
      <ol className="paper-toc">
        {shown.map((p) => (
          <li key={p.pid}>
            <div className="paper-row">
              <Link to={`/blog/post/${p.pid}`} className="paper-ptitle">{p.title}</Link>
              <span className="paper-leader" />
              <span className="paper-num">{p.pid}</span>
            </div>
            <div className="paper-meta">
              {p.pubDate} · {tagsOf(p).map((t, i) => (
                <span key={t}>{i > 0 && ' / '}<Link to={to({ tags: [t] })}>{t}</Link></span>
              ))}
            </div>
          </li>
        ))}
        {shown.length === 0 && <li><em>Nothing matches. <Link to="/">Clear filters</Link></em></li>}
      </ol>

      {pages > 1 && (
        <nav className="paper-pages" aria-label="Pages">
          {cur > 1 ? <Link to={to({ page: cur - 1 })}>‹ prev</Link> : <span className="off">‹ prev</span>}
          {pageList(cur, pages).map((n) =>
            typeof n === 'string'
              ? <span key={n} className="off">…</span>
              : <Link key={n} to={to({ page: n })} className={n === cur ? 'on' : ''} aria-current={n === cur ? 'page' : undefined}>{n}</Link>)}
          {cur < pages ? <Link to={to({ page: cur + 1 })}>next ›</Link> : <span className="off">next ›</span>}
        </nav>
      )}
    </div>
  );
};

export default PaperHome;
